import React from 'react';
import { X, MapPin, Barcode, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingItem } from '../types';
import { GroundedDeal } from '../services/geminiService';
import { SmartImage } from './common/SmartImage';

interface GroundedDealsModalProps {
  item: ShoppingItem | null;
  isOpen: boolean;
  onClose: () => void;
  currencySymbol: string;
}

export const GroundedDealsModal: React.FC<GroundedDealsModalProps> = ({
  item,
  isOpen,
  onClose,
  currencySymbol
}) => {
  const { t } = useTranslation();

  if (!item) return null;

  const deals: GroundedDeal[] = item.metadata?.groundedDeals || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[#0c141d]/60 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[85vh] z-10"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4 flex justify-between items-start border-b border-gray-50 flex-shrink-0">
              <div>
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  <span className="text-[9px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full tracking-widest inline-block">
                    {t('basket.grounding_deals_badge', { defaultValue: 'Local Offers' })}
                  </span>
                  {item.metadata?.userLocationAddress && (
                    <span className="text-[9px] font-bold text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <MapPin size={8} className="text-gray-400" />
                      {item.metadata.userLocationAddress}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-black text-[#1a2b3c] leading-tight">
                  {item.name}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-all active:scale-90"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 scrollbar-hide">
              {deals.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-3">
                    <ShoppingBag size={28} />
                  </div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {t('basket.no_deals_available', { defaultValue: 'No comparison deals found.' })}
                  </p>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {deals.map((deal, idx) => {
                    const priceDifference = item.price - deal.price;
                    let diffBadge = null;
                    if (priceDifference > 0) {
                      diffBadge = (
                        <span className="text-[10px] font-black uppercase text-emerald-600 bg-emerald-50 border border-emerald-100/50 px-1.5 py-0.5 rounded-full shrink-0">
                          {t('common.save')} {currencySymbol}{priceDifference.toFixed(2)}
                        </span>
                      );
                    } else if (priceDifference < 0) {
                      diffBadge = (
                        <span className="text-[10px] font-black uppercase text-rose-600 bg-rose-50 border border-rose-100/50 px-1.5 py-0.5 rounded-full shrink-0">
                          +{currencySymbol}{Math.abs(priceDifference).toFixed(2)}
                        </span>
                      );
                    } else {
                      diffBadge = (
                        <span className="text-[10px] font-black uppercase text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded-full shrink-0">
                          {t('common.same', { defaultValue: 'Same' })}
                        </span>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        className="bg-gray-50/50 border border-gray-100 rounded-[1.75rem] p-3.5 flex items-start gap-3 shadow-sm text-left"
                      >
                        {/* Image Preview */}
                        <div className="w-16 h-16 bg-white border border-gray-200/50 rounded-2xl overflow-hidden shrink-0 flex items-center justify-center relative">
                          {deal.imageUrl ? (
                            <SmartImage
                              imageUrl={deal.imageUrl}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-gray-300">
                              <ShoppingBag size={20} />
                            </div>
                          )}
                        </div>

                        {/* Deal Details */}
                        <div className="flex-1 min-w-0 flex flex-col gap-2">
                          {/* Store, Distance & Name block */}
                          <div>
                            {/* Store & Distance Row */}
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-start gap-2 text-sm font-black text-[#1a2b3c] tracking-tight min-w-0">
                                <div className="w-8 h-8 rounded-full bg-white border border-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                                  {deal.storeLogoUrl ? (
                                    <SmartImage
                                      imageUrl={deal.storeLogoUrl}
                                      className="w-full h-full object-contain"
                                    />
                                  ) : (
                                    <ShoppingBag size={16} className="text-indigo-500" />
                                  )}
                                </div>
                                <span className="break-words">{deal.storeName}</span>
                              </div>
                              {deal.distance != null && (
                                <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full shrink-0">
                                  {deal.distance.toFixed(1)} {t('basket.mi_unit', { defaultValue: 'mi' })}
                                </span>
                              )}
                            </div>

                            {/* Deal Product Name */}
                            <h4 className="text-sm font-bold text-[#1a2b3c] leading-snug line-clamp-2" title={deal.name || deal.productName}>
                              {deal.name || deal.productName}
                            </h4>

                            {/* Metadata Row — brand dropped, it's redundant with the
                                product name and just ate space; category/size stay. */}
                            {(deal.category || deal.size) && (
                              <div className="flex flex-wrap gap-1 items-center mt-1.5">
                                {deal.category && (
                                  <span className="text-[10px] font-black uppercase text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">
                                    {deal.category}
                                  </span>
                                )}
                                {deal.size && (
                                  <span className="text-[10px] font-bold text-gray-400 bg-gray-50/50 px-1.5 py-0.5 rounded">
                                    {deal.size} {deal.unit || ''}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Price & Location Row */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-1.5 border-t border-gray-100/50">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-black text-[#1a2b3c] tracking-tight">
                                {currencySymbol}{deal.price.toFixed(2)}
                              </span>
                              {deal.pricePerUnit != null && deal.unit && (
                                <span className="text-[10px] font-bold text-gray-400">
                                  ({currencySymbol}{deal.pricePerUnit.toFixed(2)}/{deal.unit})
                                </span>
                              )}
                              {diffBadge}
                            </div>

                            {deal.storeAddress ? (
                              <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(deal.storeAddress)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title={t('basket.get_directions', { defaultValue: 'Get directions' })}
                                className="text-[11px] text-gray-400 hover:text-indigo-600 font-bold truncate max-w-[120px] flex items-center gap-1 transition-colors"
                              >
                                <MapPin size={10} className="text-gray-300 shrink-0" />
                                {deal.storeAddress.split(',')[0]}
                              </a>
                            ) : (deal.upc || deal.gtin || deal.plu) ? (
                              <span className="flex items-center gap-1 text-[10px] text-gray-400 font-bold bg-white border border-gray-100 px-1.5 py-0.5 rounded">
                                <Barcode size={10} />
                                <span className="truncate max-w-[80px]">{deal.upc || deal.gtin || deal.plu}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-5 bg-gray-50/50 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                onClick={onClose}
                className="w-full bg-[#1a2b3c] text-white h-11 rounded-2xl font-black text-xs uppercase tracking-widest shadow-md hover:bg-emerald-600 active:scale-[0.98] transition-all"
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
